import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '../api/auth';

const AuthContext = createContext(null);

const TOKEN_KEY = 'accessToken';
const REFRESH_KEY = 'refreshToken';

export const AuthProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [menuAccess, setMenuAccess] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const applySession = useCallback((data) => {
    localStorage.setItem(TOKEN_KEY, data.accessToken);
    localStorage.setItem(REFRESH_KEY, data.refreshToken);
    setUser(data.user);
    setPermissions(data.user.permissions || []);
    setMenuAccess(data.user.menuAccess || []);
    setIsAuthenticated(true);
  }, []);

  const clearSession = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    setUser(null);
    setPermissions([]);
    setMenuAccess([]);
    setIsAuthenticated(false);
    queryClient.clear();
  }, [queryClient]);

  const fetchMe = useCallback(async () => {
    const { data } = await authApi.getMe();
    setUser(data.data);
    setPermissions(data.data.permissions || []);
    setMenuAccess(data.data.menuAccess || []);
    setIsAuthenticated(true);
    return data.data;
  }, []);

  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        await fetchMe();
      } catch {
        clearSession();
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [fetchMe, clearSession]);

  const login = useCallback(async (email, password, options = {}) => {
    const { data } = await authApi.login(email, password, options);
    applySession(data.data);
    return data.data.user;
  }, [applySession]);

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    try {
      if (refreshToken) await authApi.logout(refreshToken);
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const hasPermission = useCallback((code) => permissions.includes(code), [permissions]);
  const canAccessMenu = useCallback((menuKey) => menuAccess.includes(menuKey), [menuAccess]);

  return (
    <AuthContext.Provider value={{
      user,
      permissions,
      menuAccess,
      role: user?.role?.code,
      isAuthenticated,
      loading,
      login,
      logout,
      fetchMe,
      hasPermission,
      canAccessMenu,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
